import { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Award,
  BookOpen,
  Check,
  ChevronDown,
  Lock,
  Play,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "components/ui/button";
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import {
  AssignedCourseSummary,
  CourseChapter,
  CourseProgressData,
  CourseProgressFlow,
} from "types";
import { buildFlowPath } from "utils/flowNavigation";
import { CourseRequirements } from "./CourseRequirements";
import { CourseJourney } from "./CourseJourney";
import { LessonThumbnail } from "./LessonThumbnail";
import {
  CourseCover,
  CourseProgressBar,
  courseCounts,
  courseEligibility,
  courseImage,
  courseState,
  percent,
} from "./coursePresentation";

function LessonRow({
  flow,
  index,
  courseId,
  locked,
  courseLockReason,
}: {
  flow: CourseProgressFlow;
  index: number;
  courseId: number;
  locked: boolean;
  courseLockReason: string;
}) {
  const navigate = useNavigate();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const done = Boolean(flow.completed_at);
  const accessible = !locked && (done || (!flow.is_locked && flow.is_current));
  const title = flow.flow_details.title || flow.flow_details.name;
  const description = flow.flow_details.description?.trim();
  const showDescription = Boolean(description) &&
    ![title, flow.flow_details.name].some((value) => value.trim() === description);
  const isQuiz = flow.flow_details.is_quiz;
  const duration = flow.flow_details.estimated_duration_minutes;
  const disabledReason = locked
    ? courseLockReason
    : "Finish the earlier modules to unlock this one.";
  const lesson = (
      <button
        type="button"
        className={`course-lesson${flow.is_current && accessible ? " is-current" : ""}${accessible ? " is-accessible" : " is-locked"}`}
        aria-disabled={!accessible || undefined}
        onClick={(event) => {
          if (!accessible) {
            event.preventDefault();
            setTooltipOpen(true);
            return;
          }
          navigate(
            buildFlowPath({ courseId, flowName: flow.flow_details.name }),
          );
        }}
        data-testid={`course-flow-${flow.id}`}
      >
        <span className="lesson-visual" aria-hidden="true">
          <LessonThumbnail flow={flow} />
          {done && (
            <span className="lesson-marker is-complete">
              <Check size={14} />
            </span>
          )}
        </span>
        <span className="lesson-copy">
          <strong>{title}</strong>
          {showDescription && (
            <span className="lesson-description" data-testid={`course-flow-description-${flow.id}`}>
              {description}
            </span>
          )}
          <span className="lesson-meta">
            {String(index + 1).padStart(2, "0")} · {" "}
            {isQuiz
              ? flow.flow_details.passing_score_percentage == null
                ? "Survey"
                : "Quiz · Pass to complete"
              : "Module"}
            {typeof duration === "number" && Number.isFinite(duration) && duration > 0
              ? ` · ${duration} min`
              : null}
          </span>
        </span>
        {accessible && (
          <span className="lesson-play" aria-hidden="true">
            {done ? <ArrowRight size={16} /> : <Play size={16} />}
          </span>
        )}
      </button>
  );
  return (
    <li>
      {accessible ? lesson : (
        <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
          <TooltipTrigger asChild>{lesson}</TooltipTrigger>
          <TooltipContent className="course-lesson-tooltip" side="top" sideOffset={8} collisionPadding={16}>
            {disabledReason}
            <TooltipArrow className="course-lesson-tooltip-arrow" width={10} height={5} />
          </TooltipContent>
        </Tooltip>
      )}
    </li>
  );
}

function ChapterSection({
  chapter,
  number,
  flows,
  allFlows,
  current,
  courseId,
  locked,
  courseLockReason,
}: {
  chapter: CourseChapter;
  number: number;
  flows: CourseProgressFlow[];
  allFlows: CourseProgressFlow[];
  current: boolean;
  courseId: number;
  locked: boolean;
  courseLockReason: string;
}) {
  const [open, setOpen] = useState(current);
  const complete = flows.filter((flow) => Boolean(flow.completed_at)).length;
  const panelId = `course-${courseId}-chapter-${chapter.id}`;
  return (
    <section className="course-chapter">
      <h3>
        <button
          className="chapter-toggle"
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((value) => !value)}
        >
          <span
            className={`chapter-marker${complete === flows.length ? " is-complete" : ""}`}
            aria-hidden="true"
          >
            {complete === flows.length ? (
              <Check size={18} />
            ) : (
              <BookOpen size={18} />
            )}
          </span>
          <span className="chapter-copy">
            <span className="course-eyebrow">
              Chapter {number}
            </span>
            <strong>{chapter.title}</strong>
            {chapter.description && <span>{chapter.description}</span>}
          </span>
          <span className="chapter-progress">
            {complete} of {flows.length} complete
          </span>
          <ChevronDown
            className={open ? "chapter-chevron is-open" : "chapter-chevron"}
            size={19}
          />
        </button>
      </h3>
      <div id={panelId} hidden={!open}>
        <ol className="course-lessons">
          {flows.map((flow) => (
            <LessonRow
              key={flow.id}
              flow={flow}
              index={allFlows.indexOf(flow)}
              courseId={courseId}
              locked={locked}
              courseLockReason={courseLockReason}
            />
          ))}
        </ol>
      </div>
    </section>
  );
}

export function CourseOverview({
  summary,
  progress,
  onRetry,
}: {
  summary: AssignedCourseSummary;
  progress?: CourseProgressData;
  onRetry: () => void;
}) {
  const navigate = useNavigate();
  const eligibility = courseEligibility(summary, progress);
  const locked = Boolean(eligibility?.is_locked);
  const prerequisiteNames = eligibility?.unmet_prerequisites.map((item) => `“${item.name}”`);
  const courseLockReason = prerequisiteNames?.length
    ? `Complete ${prerequisiteNames.join(" and ")} to unlock this course.`
    : "Complete this course’s prerequisites to unlock its modules.";
  const state = courseState(summary, progress);
  const flows = [...(progress?.flows ?? [])].sort((a, b) => a.order - b.order);
  const chapters = [
    ...(progress?.chapters ?? progress?.course?.chapters ?? []),
  ].sort((a, b) => a.order - b.order);
  const current = flows.find((flow) => flow.is_current && !flow.is_locked);
  const currentChapter = chapters.find(
    (chapter) => chapter.id === current?.chapter_id,
  );
  const currentTitle =
    current?.flow_details.title || current?.flow_details.name;
  const actionLabel =
    state === "completed"
      ? "View course completion"
      : (current?.has_started || (current?.response_count ?? 0) > 0)
        ? "Continue module"
        : "Start module";
  const action = () => {
    if (locked) return;
    if (state === "completed")
      navigate(`/course_complete?courseId=${summary.id}`);
    else if (current)
      navigate(
        buildFlowPath({
          courseId: summary.id,
          flowName: current.flow_details.name,
        }),
      );
  };
  const showAction = !locked && (Boolean(current) || state === "completed");
  const displayedChapterIds = new Set<number>();
  const outline = flows.map((flow, index) => {
    const chapter = chapters.find((item) => item.id === flow.chapter_id);
    if (!chapter)
      return (
        <ol className="course-ungrouped course-lessons" key={`flow-${flow.id}`}>
          <LessonRow
            flow={flow}
            index={index}
            courseId={summary.id}
            locked={locked}
            courseLockReason={courseLockReason}
          />
        </ol>
      );
    if (displayedChapterIds.has(chapter.id)) return null;
    displayedChapterIds.add(chapter.id);
    return (
      <ChapterSection
        key={chapter.id}
        chapter={chapter}
        number={displayedChapterIds.size}
        flows={flows.filter((item) => item.chapter_id === chapter.id)}
        allFlows={flows}
        current={current?.chapter_id === chapter.id}
        courseId={summary.id}
        locked={locked}
        courseLockReason={courseLockReason}
      />
    );
  });
  return (
    <TooltipProvider delayDuration={250}>
    <div className={`course-overview${showAction ? " has-mobile-action" : ""}`}>
      <Link to="/training#courses" className="course-back">
        <ArrowLeft size={16} />
        Back to courses
      </Link>
      <section
        className="course-overview-hero course-glass"
        aria-labelledby="course-title"
      >
        <CourseCover
          courseId={summary.id}
          name={summary.name}
          source={courseImage(progress?.course)}
        />
        <div className="course-hero-copy">
          <div className="course-hero-title">
            {locked && <Lock size={22} role="img" aria-label="Course locked" />}
            <h1 id="course-title" tabIndex={-1}>{summary.name}</h1>
          </div>
          {progress?.course?.description && (
            <p>{progress.course.description}</p>
          )}
          {Boolean(progress?.course?.learning_outcomes?.length) && (
            <div className="course-learning-outcomes">
              <h2>What you’ll learn</h2>
              <ul>
                {progress?.course?.learning_outcomes?.map((outcome, index) => (
                  <li key={index}>{outcome}</li>
                ))}
              </ul>
            </div>
          )}
          <CourseProgressBar progress={progress} summary={summary} />
          <p className="course-muted">
            {percent(progress?.progress_percent ?? summary.progress_percent)}%
            complete
            {locked &&
            (progress?.progress_percent ?? summary.progress_percent) > 0
              ? " · Progress saved"
              : ""}
          </p>
          {showAction && (
            <div className="course-next-action">
              <Button size="auto" onClick={action}>
                {state === "completed" ? (
                  <Award size={18} />
                ) : (
                  <Play size={16} />
                )}
                {actionLabel}
              </Button>
              {current && (
                <span>
                  Up next: {currentTitle}
                  {currentChapter ? ` · ${currentChapter.title}` : ""}
                </span>
              )}
            </div>
          )}
          {progress?.has_issued_certificate && state !== "completed" && (
            <Link className="course-text-link" to={`/course_complete?courseId=${summary.id}`}>
              View previous completion
            </Link>
          )}
          {!progress && (
            <div role="alert">
              <p>We couldn’t load this course’s outline.</p>
              <Button variant="secondary" size="auto" onClick={onRetry}>
                Try again
              </Button>
            </div>
          )}
        </div>
      </section>
      {eligibility && locked && (
        <CourseRequirements eligibility={eligibility} />
      )}
      <CourseJourney chapters={chapters} flows={flows} locked={locked} />
      <section
        className="course-curriculum"
        aria-labelledby="curriculum-heading"
      >
        <div className="course-section-heading">
          <h2 id="curriculum-heading">Curriculum</h2>
          <span>{courseCounts(progress)}</span>
        </div>
        {flows.length ? (
          outline
        ) : progress ? (
          <div className="course-empty">
            <BookOpen size={30} />
            <h3>Modules are on their way</h3>
            <p>This course doesn’t have any modules yet. Check back soon.</p>
          </div>
        ) : null}
      </section>
      {showAction && (
        <div className="course-mobile-action">
          <div>
            <strong>{actionLabel}</strong>
            <span>{currentTitle || summary.name}</span>
          </div>
          <Button
            size="auto"
            onClick={action}
            aria-label={`${actionLabel} — ${currentTitle || summary.name}`}
          >
            <ArrowRight size={18} />
            <span className="sr-only">Go</span>
          </Button>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}
